import { useContext } from 'react';
import { useRouter } from 'next/router';
import { getQuizDetails, getCurrentQuizType } from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { checkForEvaluationPending } from '@models/Item';
import { QuizStatusContext } from './context';
import { SUBJECTIVE_UPLOAD_STATUS } from '../../constants';

const useQuizStatus = () => {
  const { query } = useRouter();
  const { quizUID } = query;

  const { status, handleIsLiveChange } = useContext(QuizStatusContext);
  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const { isSubjective } = useSelectorWithShallowEqual(getCurrentQuizType);

  const hasAttempted = !!quizDetails.previous_sessions?.length;
  const { isLive, isInFuture, isToday } = status;

  const isHeldInFuture = isInFuture && !isToday;
  const isLiveAndAttempted = isLive && hasAttempted;
  const toBeHeldToday = !isLive && isToday && isInFuture;

  const isEvaluationPending = isSubjective
    ? !quizDetails?.previous_sessions?.[0]?.expired &&
      quizDetails?.latest_session?.evaluation_state !== undefined &&
      checkForEvaluationPending(quizDetails?.latest_session?.evaluation_state)
    : false;

  const isSubjectiveAnswerUploadPending = isSubjective
    ? quizDetails.allow_answer_upload &&
      quizDetails.previous_sessions?.[0]?.upload_state ===
        SUBJECTIVE_UPLOAD_STATUS.PENDING
    : false;

  const isSubjectiveAnswerUploadRejected = isSubjective
    ? quizDetails.previous_sessions?.[0]?.upload_state ===
      SUBJECTIVE_UPLOAD_STATUS.DECLINED
    : false;

  const isSubjectiveAnswerUploaded = isSubjective
    ? quizDetails.allow_answer_upload &&
      quizDetails.previous_sessions?.[0]?.upload_state ===
        SUBJECTIVE_UPLOAD_STATUS.DONE
    : false;

  return {
    isLive,
    isInFuture,
    isToday,
    hasAttempted,
    isHeldInFuture,
    isLiveAndAttempted,
    toBeHeldToday,
    isSubjectiveAnswerUploadRejected,
    isSubjectiveAnswerUploadPending,
    isSubjectiveAnswerUploaded,
    isEvaluationPending,
    handleIsLiveChange
  };
};

export default useQuizStatus;
